// The capture state machine as a pure reducer. The driver and the hook turn
// its transitions into side effects; the reducer decides everything a child
// would notice — when the "almost done" cue shows, when the recording is cut
// off, and whether what was recorded is kept. docs/plan/
// speech-transcription-package.md § "The capture state machine".
import type { CaptureError, CaptureLimits, CaptureMeta } from "../contract";
import type { PermissionLikeError, StartFailure } from "./errors";
import type { Capability, UnsupportedReason } from "./negotiate";

// What a finished capture hands on. The Blob is never parsed by zod, so this
// is a plain type here rather than a schema in the contract; `blob.type` is
// `meta.mime`.
export interface Recording {
  readonly meta: CaptureMeta;
  readonly blob: Blob;
}

export type RejectedError = Extract<
  CaptureError,
  { kind: "too-short" | "too-large" | "recorder-failed" }
>;

export type CaptureState =
  | { readonly tag: "unsupported"; readonly reason: UnsupportedReason }
  | { readonly tag: "idle" } // ready; nothing recorded
  | { readonly tag: "requesting-permission" } // getUserMedia pending; a prompt may be up
  | { readonly tag: "blocked"; readonly error: PermissionLikeError }
  | {
      readonly tag: "recording";
      // On the driver's clock (performance.now()), as are ticks.
      readonly startedAt: number;
      readonly warned: boolean;
    }
  // Stop has been asked of the recorder and its final data is pending.
  // `endedBy` lets the view say "time's up" rather than just "finishing".
  | {
      readonly tag: "stopping";
      readonly endedBy: Exclude<CaptureMeta["endedBy"], "track-ended">;
    }
  | { readonly tag: "recorded"; readonly recording: Recording } // play back, submit, or redo
  | { readonly tag: "rejected"; readonly error: RejectedError };

export type CaptureEvent =
  | { readonly type: "start-requested" }
  | { readonly type: "permission-granted"; readonly at: number }
  | { readonly type: "permission-failed"; readonly error: StartFailure }
  | { readonly type: "tick"; readonly at: number } // drives warn + auto-stop
  | { readonly type: "stop-requested" }
  // Also arrives straight from `recording` when the driver stopped on its own
  // (tab hidden, track ended); the recording's `meta.endedBy` says which.
  | { readonly type: "recorder-stopped"; readonly recording: Recording }
  | { readonly type: "recorder-failed" }
  | { readonly type: "redo-requested" };

export const initialCaptureState = (capability: Capability): CaptureState =>
  capability.tag === "supported"
    ? { tag: "idle" }
    : { tag: "unsupported", reason: capability.reason };

// The durable consequence of a capture: kept, or refused before upload. Too
// short is checked first — a sub-second blob is never interesting for its
// size — and neither refusal costs a quota unit or a provider call.
const settle = (recording: Recording, limits: CaptureLimits): CaptureState => {
  const { durationMs, bytes } = recording.meta;
  if (durationMs < limits.minDurationMs)
    return { tag: "rejected", error: { kind: "too-short", durationMs } };
  if (bytes > limits.maxBytes)
    return { tag: "rejected", error: { kind: "too-large", bytes } };
  return { tag: "recorded", recording };
};

// Unhandled (state, event) pairs return `s` itself, not a copy: a stray tick
// or a double tap is a no-op, and the hook skips re-rendering on identity.
export const reduceCapture = (
  s: CaptureState,
  e: CaptureEvent,
  limits: CaptureLimits,
): CaptureState => {
  switch (e.type) {
    case "start-requested":
      // From `blocked` too: an adult may have re-enabled the mic or closed
      // the app holding it, and getUserMedia is the only way to find out.
      return s.tag === "idle" || s.tag === "blocked" || s.tag === "rejected"
        ? { tag: "requesting-permission" }
        : s;
    case "permission-granted":
      return s.tag === "requesting-permission"
        ? { tag: "recording", startedAt: e.at, warned: false }
        : s;
    case "permission-failed":
      if (s.tag !== "requesting-permission") return s;
      return e.error.kind === "recorder-failed"
        ? { tag: "rejected", error: e.error }
        : { tag: "blocked", error: e.error };
    case "tick": {
      if (s.tag !== "recording") return s;
      const elapsed = e.at - s.startedAt;
      if (elapsed >= limits.maxDurationMs)
        return { tag: "stopping", endedBy: "max-duration" };
      if (!s.warned && elapsed >= limits.warnAtMs)
        return { ...s, warned: true };
      return s;
    }
    case "stop-requested":
      return s.tag === "recording" ? { tag: "stopping", endedBy: "user" } : s;
    case "recorder-stopped":
      return s.tag === "recording" || s.tag === "stopping"
        ? settle(e.recording, limits)
        : s;
    case "recorder-failed":
      return s.tag === "requesting-permission" ||
        s.tag === "recording" ||
        s.tag === "stopping"
        ? { tag: "rejected", error: { kind: "recorder-failed" } }
        : s;
    case "redo-requested":
      return s.tag === "recorded" || s.tag === "rejected" ? { tag: "idle" } : s;
  }
};
